import { useNavigate } from 'react-router';
import { useEffect } from 'react';
import { GreetingSection } from 'components/home/GreetingSection';
import { InviteCodeButton } from 'components/home/InviteCodeButton';
import { MeetingSection } from 'components/home/MeetingSection';
import { EmptyMeetingState } from 'components/home/EmptyMeetingState';
import { BottomNavigation } from 'components/common/bottomNavigation/BottomNavigation';
import { Meeting } from 'components/home/MeetingCard';
import { useMeetingList } from 'hooks/useMeeting';
import { meetingStore } from 'store/meetingStore';
import { useUserInfo } from 'hooks/useAuth';
import './Home.css';

export const Home = () => {
    const navigate = useNavigate();
    const { nickname } = useUserInfo();
    const { data } = useMeetingList();

    // 컴포넌트 마운트 시 모임 목록 조회
    useEffect(() => {
        meetingStore.onGetMeetingList();
    }, []);

    // 암호화 저장된 사용자 정보에서 닉네임 사용
    const userName = nickname || "사용자";

    // API 데이터를 Meeting 타입으로 변환
    const meetings: Meeting[] = data?.data?.map((meeting: any) => {
        // 일정 날짜 기반 상태 계산
        const calculateStatus = () => {
            if (!meeting.schedule) return "예정";

            const now = new Date();
            const startDate = new Date(meeting.schedule.startDatetime);
            const endDate = new Date(meeting.schedule.endDatetime);

            // 이미 종료됨
            if (now > endDate) return "종료";

            // 현재 진행 중 (시작일 ~ 종료일 사이)
            if (now >= startDate && now <= endDate) return "진행중";

            // 아직 시작 전
            return "예정";
        };

        return {
            id: meeting.meetingId,
            title: meeting.meetingName,
            host: meeting.creatorNickname || "알 수 없음",
            status: calculateStatus(),
            time: meeting.schedule ?
                `${new Date(meeting.schedule.startDatetime).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} - ${new Date(meeting.schedule.endDatetime).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}` :
                "",
            date: meeting.schedule ?
                new Date(meeting.schedule.startDatetime).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit' }) :
                "",
            location: meeting.place?.placeAddress || meeting.place?.placeName || "",
            dDay: meeting.schedule ?
                (() => {
                    const today = new Date();
                    const meetingDate = new Date(meeting.schedule.startDatetime);
                    const diffTime = meetingDate.getTime() - today.getTime();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays > 0 ? `D-${diffDays}` : diffDays === 0 ? "D-Day" : `D+${Math.abs(diffDays)}`;
                })() :
                undefined,
            isRegularMeeting: meeting.isRegularMeeting || false,
            scheduleStartDateTime: meeting.schedule?.startDatetime,
            scheduleEndDateTime: meeting.schedule?.endDatetime
        };
    }) || [];

    // 날짜 기반으로 모임 필터링
    const today = new Date();
    const todayStart = new Date(today);
    todayStart.setHours(0, 0, 0, 0);
    const tomorrow = new Date(todayStart);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const upcomingLimit = new Date(todayStart);
    upcomingLimit.setDate(upcomingLimit.getDate() + 7); // 7일 이내

    // 1시간 이내 시작하거나 진행 중인 모임 찾기 (종료 시간까지 배너 노출)
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    const upcomingSoonMeeting = meetings.find(m => {
        if (!m.scheduleStartDateTime) return false;
        const meetingTime = new Date(m.scheduleStartDateTime);
        const endTime = m.scheduleEndDateTime ? new Date(m.scheduleEndDateTime) : null;

        // 종료 시간이 지났으면 제외
        if (endTime && endTime < now) return false;

        // 1시간 이내 시작 예정이거나, 이미 시작했지만 아직 종료되지 않은 모임
        return meetingTime <= oneHourLater;
    });

    // 일정 기준 정렬 함수 (가까운 순)
    const sortBySchedule = (a: Meeting, b: Meeting) => {
        // 일정이 없는 모임은 뒤로
        if (!a.scheduleStartDateTime && !b.scheduleStartDateTime) return 0;
        if (!a.scheduleStartDateTime) return 1;
        if (!b.scheduleStartDateTime) return -1;
        return new Date(a.scheduleStartDateTime).getTime() - new Date(b.scheduleStartDateTime).getTime();
    };

    // TODAY: 오늘 진행 중인 모임 (시작일~종료일 사이에 오늘이 포함, 종료 시간이 지난 것은 제외)
    const todayMeetings = meetings
        .filter(m => {
            if (!m.scheduleStartDateTime) return false;

            // 시작일 (시간 제거)
            const meetingStartDate = new Date(m.scheduleStartDateTime);
            meetingStartDate.setHours(0, 0, 0, 0);

            // 종료일 (시간 제거, 없으면 시작일과 동일)
            const meetingEndDate = m.scheduleEndDateTime
                ? new Date(m.scheduleEndDateTime)
                : new Date(m.scheduleStartDateTime);
            meetingEndDate.setHours(0, 0, 0, 0);

            // 오늘이 시작일~종료일 범위에 포함되는지 확인
            if (todayStart < meetingStartDate || todayStart > meetingEndDate) return false;

            // 종료 시간이 이미 지났으면 제외 (마지막 날 종료 시간 체크)
            if (m.scheduleEndDateTime) {
                const endTime = new Date(m.scheduleEndDateTime);
                if (endTime < now) return false;
            }
            return true;
        })
        .sort(sortBySchedule);

    // UPCOMING: 내일부터 14일 이내 시작 예정인 모임 (이미 시작된 모임 제외)
    const upcomingMeetings = meetings
        .filter(m => {
            if (!m.scheduleStartDateTime) return false;
            const meetingStartDate = new Date(m.scheduleStartDateTime);
            meetingStartDate.setHours(0, 0, 0, 0);

            // 이미 시작된 모임(시작일이 오늘 이전)은 제외
            if (meetingStartDate <= todayStart) return false;

            // 내일부터 14일 이내 시작 예정
            return meetingStartDate >= tomorrow && meetingStartDate <= upcomingLimit;
        })
        .sort(sortBySchedule);

    // PARTICIPATING: 정기모임 또는 일정이 없거나 먼 미래인 모임 (진행 중이거나 예정된 모임은 제외)
    const participatingMeetings = meetings
        .filter(m => {
            // 정기모임은 무조건 PARTICIPATING에 표시
            if (m.isRegularMeeting) return true;

            // 일정이 없는 모임
            if (!m.scheduleStartDateTime) return true;

            const meetingStartDate = new Date(m.scheduleStartDateTime);
            meetingStartDate.setHours(0, 0, 0, 0);

            const meetingEndDate = m.scheduleEndDateTime
                ? new Date(m.scheduleEndDateTime)
                : new Date(m.scheduleStartDateTime);
            meetingEndDate.setHours(0, 0, 0, 0);

            // 현재 진행 중인 모임(오늘이 시작일~종료일 범위)은 제외 (TODAY에 표시됨)
            if (todayStart >= meetingStartDate && todayStart <= meetingEndDate) {
                // 단, 종료 시간이 지났으면 PARTICIPATING에 표시
                if (m.scheduleEndDateTime) {
                    const endTime = new Date(m.scheduleEndDateTime);
                    if (endTime >= now) return false; // 아직 종료 안됨 -> TODAY에 표시
                }
                return false;
            }

            // UPCOMING 범위(내일~14일 이내)인 모임도 제외
            if (meetingStartDate >= tomorrow && meetingStartDate <= upcomingLimit) return false;

            // 14일 이후 일정 또는 이미 종료된 모임
            return true;
        })
        .sort(sortBySchedule);

    // 모임까지 남은 시간 또는 진행 중 여부 계산
    const getTimeUntilMeeting = (scheduleStartDateTime: string) => {
        const meetingTime = new Date(scheduleStartDateTime);
        const diffMs = meetingTime.getTime() - now.getTime();
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        // 이미 시작한 모임
        if (diffMinutes <= 0) {
            return null; // 진행 중
        }
        if (diffMinutes < 60) {
            return `${diffMinutes}분`;
        }
        return '1시간';
    };

    const handleMeetingClick = (meeting: Meeting) => {
        navigate(`/meeting/detail/${meeting.id}`);
    };

    // TODO: 정산 데이터 API 연동 필요
    // const settlement = usePaymentSummary(); // 정산 API hook 구현 필요

    return (
        <div className="home-wrapper">
            <div className="home-header">
                <div className="home-logo">MO2</div>
                <button
                    className="notification-button"
                    onClick={() => navigate('/notifications')}
                >
                    🔔
                </button>
            </div>

            <div className="home-content">
                <GreetingSection userName={userName} />

                {/* 모임이 있을 때만 초대 코드 버튼 표시 */}
                {meetings.length > 0 && (
                    <InviteCodeButton onClick={() => navigate('/join-code')} />
                )}

                {/* TODO: 정산 섹션은 정산 API 구현 후 활성화
                <SettlementSection
                    totalCost={settlement.totalCost}
                    myPayment={settlement.myPayment}
                    onClick={() => navigate(`/cost/result/${settlement.meetingId}`)}
                />
                */}

                {/* 모임이 없을 때 EmptyState 표시 */}
                {meetings.length === 0 ? (
                    <EmptyMeetingState />
                ) : (
                    <>
                        <MeetingSection
                            title="TODAY"
                            meetings={todayMeetings}
                            onViewAll={() => navigate('/meetings?filter=today')}
                            onMeetingClick={handleMeetingClick}
                            upcomingSoonMeetingId={upcomingSoonMeeting?.id}
                            getTimeUntilMeeting={getTimeUntilMeeting}
                            onLocationClick={(meetingId) => navigate(`/meeting/location/${meetingId}`)}
                            showCreateButton={true}
                            onCreateMeeting={() => navigate('/meeting/create')}
                            emptyMessage="오늘 예정된 모임이 없어요"
                            emptySubMessage="새로운 모임을 만들어 친구들과 함께해보세요!"
                        />

                        <MeetingSection
                            title="UPCOMING"
                            meetings={upcomingMeetings}
                            onViewAll={() => navigate('/meetings?filter=upcoming')}
                            onMeetingClick={handleMeetingClick}
                        />

                        <MeetingSection
                            title="PARTICIPATING"
                            meetings={participatingMeetings}
                            onViewAll={() => navigate('/meetings?filter=participating')}
                            onMeetingClick={handleMeetingClick}
                        />
                    </>
                )}
            </div>

            <BottomNavigation />
        </div>
    );
};
